import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TableHeaderCell, ActiveFilters } from '@/components/ui/table-header-cell';
import StatusChangeModal from '@/components/StatusChangeModal';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Pin,
  FolderOpen,
  Clock,
  AlertCircle,
  RefreshCw,
  FileText,
  Sparkles
} from 'lucide-react';
import {
  SR_STATUS_CONFIG,
  SR_STATUSES,
  SR_FOLLOWUP_THRESHOLDS,
  calculateDaysInStatus,
  needsFollowup,
  WS_STATUS_CONFIG
} from '@/config/statusWorkflow';
import { EVENT_TYPES } from '@/config/eventTypes';

// Event Type filter options - use authoritative list from config
const EVENT_TYPE_OPTIONS = EVENT_TYPES.map(type => ({
  value: type,
  label: type
}));

// Status order for sorting (pre-scheduling flow + execution flow + special statuses)
// Combines SR and WS statuses for unified sorting
const STATUS_ORDER = [
  // Pre-scheduling (SR)
  'NEW', 'ESTIMATE', 'APPROVAL', 'PLANNING', 'SR_SCHEDULED',
  // Execution (WS)
  'SCHEDULED', 'IN_PROCESS', 'ON_HOLD', 'QA_REVIEW', 'RELEASE', 'BILLING', 'INVOICED', 'CLOSED',
  // Terminal/Special
  'RESCHEDULE', 'CANCELLED'
];

// Filter options: combine SR statuses (for pre-scheduling) and WS statuses (for execution)
const STATUS_OPTIONS = [
  // SR Pre-scheduling statuses (shown when no WS exists)
  { value: 'NEW', label: 'New', group: 'Pre-Scheduling' },
  { value: 'ESTIMATE', label: 'Estimate', group: 'Pre-Scheduling' },
  { value: 'APPROVAL', label: 'Approval', group: 'Pre-Scheduling' },
  { value: 'PLANNING', label: 'Planning', group: 'Pre-Scheduling' },
  // WS Execution statuses (shown when WS exists)
  { value: 'SCHEDULED', label: 'Scheduled', group: 'Execution' },
  { value: 'IN_PROCESS', label: 'In Process', group: 'Execution' },
  { value: 'ON_HOLD', label: 'On Hold', group: 'Execution' },
  { value: 'QA_REVIEW', label: 'QA Review', group: 'Execution' },
  { value: 'RELEASE', label: 'Release', group: 'Execution' },
  { value: 'BILLING', label: 'Billing', group: 'Execution' },
  { value: 'INVOICED', label: 'Invoiced', group: 'Execution' },
  { value: 'CLOSED', label: 'Closed', group: 'Execution' },
  // Special statuses
  { value: 'RESCHEDULE', label: 'Reschedule', group: 'Special' },
  { value: 'CANCELLED', label: 'Cancelled', group: 'Special' },
];

/**
 * Get the "effective" status for display and filtering
 * WS status takes precedence if Work Schedule exists
 */
const getEffectiveStatus = (request) => {
  return request.work_schedule_status || request.status;
};

/**
 * Get Work Schedule status config for display
 */
const getWsStatusConfig = (status) => {
  return WS_STATUS_CONFIG[status] || { label: status || '-', color: 'bg-gray-100 text-gray-800' };
};

const ServiceRequestsPage = () => {
  const navigate = useNavigate();
  const { user, api, currentFacility, hasRole } = useAuth();
  const [requests, setRequests] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  
  // Status change modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedSR, setSelectedSR] = useState(null);
  
  // Sort state — 2026-07-13: tri-state clicks in <TableHeaderCell> call
  // onSort(null) on the third click. Coerce that back to the default sort
  // instead of leaving `sort` null, which was crashing `a[sort.field]` below.
  const DEFAULT_SORT = { field: 'created_at', direction: 'desc' };
  const [sort, setSortRaw] = useState(DEFAULT_SORT);
  const setSort = (next) => setSortRaw(next || DEFAULT_SORT);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: [],
    facility_id: [],
    event_type: []
  });

  // Check if "All facilities" is selected and user is Manager+
  const showFacilityColumn = currentFacility === 'all' && hasRole(['ADMIN', 'MANAGER']);
  
  // Operators and above can see the SR list
  const canViewSRList = hasRole(['ADMIN', 'MANAGER', 'OPERATOR']);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_archived: includeArchived };
      if (currentFacility && currentFacility !== 'all') {
        params.facility_id = currentFacility;
      }
      const response = await api.get('/service-requests', { params });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch service requests:', error);
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  }, [api, currentFacility, includeArchived]);

  const fetchFacilities = useCallback(async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchRequests();
    fetchFacilities();
  }, [fetchRequests, fetchFacilities]);

  // Build facility filter options
  const facilityOptions = useMemo(() => {
    return facilities.map(f => ({ value: f.id, label: f.name }));
  }, [facilities]);

  // Calculate days open from creation date (global rule)
  const getDaysOpen = (request) => {
    if (!request.created_at) return 0;
    try {
      const created = new Date(request.created_at);
      const now = new Date();
      return Math.floor((now - created) / (1000 * 60 * 60 * 24));
    } catch {
      return 0;
    }
  };

  // Calculate days in current status (for follow-up)
  const getDaysInStatus = (request) => {
    const statusKey = `status_${request.status?.toLowerCase()}_at`;
    const timestamp = request[statusKey] || request.created_at;
    return calculateDaysInStatus(timestamp);
  };

  // Apply filters and sorting
  const filteredAndSortedRequests = useMemo(() => {
    let result = [...requests];

    // Apply search
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(r =>
        r.job_number?.toLowerCase().includes(searchLower) ||
        r.car_mark?.toLowerCase().includes(searchLower) ||
        r.car_number?.toLowerCase().includes(searchLower) ||
        r.car_owner?.toLowerCase().includes(searchLower) ||
        r.repair_site?.toLowerCase().includes(searchLower) ||
        r.site_name?.toLowerCase().includes(searchLower) ||
        r.requestor_name?.toLowerCase().includes(searchLower) ||
        r.requestor_company?.toLowerCase().includes(searchLower) ||
        r.event_type?.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters using effective status (WS status if exists, otherwise SR status)
    if (filters.status.length > 0) {
      result = result.filter(r => {
        const effectiveStatus = getEffectiveStatus(r);
        return filters.status.includes(effectiveStatus);
      });
    }
    if (filters.facility_id.length > 0) {
      result = result.filter(r => filters.facility_id.includes(r.facility_id));
    }
    // Event Type filter
    if (filters.event_type.length > 0) {
      result = result.filter(r => filters.event_type.includes(r.event_type));
    }

    // Sort pinned items first, then apply sort
    // 2026-07-13 — defensive guard: if sort is somehow null/undefined, skip sorting.
    const activeSort = sort && sort.field ? sort : null;
    result.sort((a, b) => {
      // Pinned items always first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      if (!activeSort) return 0;

      let aVal = a[activeSort.field];
      let bVal = b[activeSort.field];

      // Handle special sort orders
      if (activeSort.field === 'status') {
        // Sort by effective status (WS status if exists, otherwise SR status)
        const aStatus = getEffectiveStatus(a);
        const bStatus = getEffectiveStatus(b);
        aVal = STATUS_ORDER.indexOf(aStatus);
        bVal = STATUS_ORDER.indexOf(bStatus);
        if (aVal === -1) aVal = 999;
        if (bVal === -1) bVal = 999;
      } else if (activeSort.field === 'created_at' || activeSort.field === 'call_date') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (activeSort.field === 'car_number') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      } else if (activeSort.field === 'days_open') {
        aVal = getDaysOpen(a);
        bVal = getDaysOpen(b);
      }

      // String comparison for other fields
      if (typeof aVal === 'string') aVal = aVal?.toLowerCase() || '';
      if (typeof bVal === 'string') bVal = bVal?.toLowerCase() || '';

      if (aVal < bVal) return activeSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return activeSort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [requests, search, filters, sort]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleFilterChange = (field, values) => {
    setFilters(prev => ({ ...prev, [field]: values }));
  };

  const handleClearFilter = (field) => {
    setFilters(prev => ({ ...prev, [field]: [] }));
  };

  const handleClearAllFilters = () => {
    setFilters({ status: [], facility_id: [], event_type: [] });
  };

  const handlePin = async (id) => {
    try {
      const response = await api.put(`/service-requests/${id}/pin`);
      toast.success(response.data.message);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update pin status');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/service-requests/${deleteId}`);
      toast.success('Service request deleted');
      setDeleteId(null);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete service request');
    }
  };

  const openStatusModal = (request) => {
    setSelectedSR(request);
    setStatusModalOpen(true);
  };

  const handleStatusChanged = () => {
    fetchRequests();
  };

  const getStatusConfig = (status) => {
    return SR_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  };

  const canManage = hasRole(['ADMIN', 'MANAGER', 'OPERATOR']) || (user?.role === 'TECHNICIAN' && user?.can_create_service_requests === true);
  const isTechnician = user?.role === 'TECHNICIAN';

  return (
    <div className="space-y-4 animate-fade-in" data-testid="service-requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">Service Requests</h1>
          <p className="text-[#5A5A5A] text-sm mt-1">
            {filteredAndSortedRequests.length} request{filteredAndSortedRequests.length !== 1 ? 's' : ''}
            {requests.length !== filteredAndSortedRequests.length && ` (filtered from ${requests.length})`}
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => navigate('/service-requests/add')}
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            data-testid="add-service-request-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service Request
          </Button>
        )}
      </div>

      {/* Search and Active Filters */}
      <div className="space-y-3">
        <form onSubmit={handleSearch} className="flex gap-2 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <Input
              placeholder="Search by SR #, car, owner, repair site, requestor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#5A5A5A] cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300"
              data-testid="include-archived-toggle"
            />
            Show Archived
          </label>
        </form>

        <ActiveFilters
          filters={filters}
          onClear={handleClearFilter}
          onClearAll={handleClearAllFilters}
        />
      </div>

      {/* Table - Column order: SR#, Car, Event type, Repair Site, Requestor, Car owner, Days open, Status, Actions */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8] mx-auto"></div>
              <p className="text-[#5A5A5A] mt-4">Loading...</p>
            </div>
          ) : filteredAndSortedRequests.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#323033] mb-2">No service requests found</h3>
              <p className="text-[#5A5A5A] mb-6">
                {requests.length > 0 ? 'Try adjusting your filters' : 'Create your first service request to get started'}
              </p>
              {canManage && requests.length === 0 && (
                <Button
                  onClick={() => navigate('/service-requests/add')}
                  className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Service Request
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    {/* Facility column FIRST - only for Manager+ when "All facilities" selected */}
                    {showFacilityColumn && (
                      <TableHeaderCell
                        label="Facility"
                        field="facility_name"
                        sortable
                        filterable
                        filterOptions={facilityOptions}
                        currentSort={sort}
                        currentFilters={filters.facility_id}
                        onSort={setSort}
                        onFilter={(_, values) => handleFilterChange('facility_id', values)}
                      />
                    )}
                    {/* 1. SR # */}
                    <TableHeaderCell
                      label="SR #"
                      field="job_number"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 2. Car */}
                    <TableHeaderCell
                      label="Car"
                      field="car_mark"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 3. Event Type */}
                    <TableHeaderCell
                      label="Event Type"
                      field="event_type"
                      sortable
                      filterable
                      filterOptions={EVENT_TYPE_OPTIONS}
                      currentSort={sort}
                      currentFilters={filters.event_type}
                      onSort={setSort}
                      onFilter={handleFilterChange}
                    />
                    {/* 4. Repair Site (unified naming) */}
                    <TableHeaderCell
                      label="Repair Site"
                      field="repair_site"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 5. Requestor */}
                    <TableHeaderCell
                      label="Requestor"
                      field="requestor_name"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 6. Car Owner */}
                    <TableHeaderCell
                      label="Car Owner"
                      field="car_owner"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 7. Days Open (based on creation date) */}
                    <TableHeaderCell
                      label="Days Open"
                      field="days_open"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 8. Status */}
                    <TableHeaderCell
                      label="Status"
                      field="status"
                      sortable
                      filterable
                      filterOptions={STATUS_OPTIONS}
                      currentSort={sort}
                      currentFilters={filters.status}
                      onSort={setSort}
                      onFilter={handleFilterChange}
                    />
                    {/* 9. Actions — hidden for technicians (Wave 1 #5) */}
                    {!isTechnician && <th className="text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedRequests.map((request) => {
                    const statusConfig = getStatusConfig(request.status);
                    const daysOpen = getDaysOpen(request);
                    const daysInStatus = getDaysInStatus(request);
                    const showFollowup = needsFollowup(request.status, daysInStatus);
                    
                    // Days open color based on age
                    const daysOpenColor = daysOpen <= 3 ? '#10B981' : daysOpen <= 7 ? '#F59E0B' : '#EF4444';
                    
                    return (
                      <tr
                        key={request.id}
                        data-testid={`sr-row-${request.id}`}
                        className={`${request.pinned ? 'bg-yellow-50' : ''} hover:bg-muted/50 cursor-pointer`}
                        onClick={() => navigate(`/service-requests/${request.id}`)}
                      >
                        {/* Facility FIRST */}
                        {showFacilityColumn && (
                          <td>
                            <span className="text-sm">{request.facility_name || '-'}</span>
                          </td>
                        )}
                        {/* 1. SR # */}
                        <td>
                          <div className="flex items-center gap-2 flex-wrap">
                            {request.pinned && <Pin className="w-3 h-3 text-yellow-600" />}
                            <span className="font-medium text-[#005EF8]">{request.job_number}</span>
                          </div>
                        </td>
                        {/* 2. Car */}
                        <td>
                          <span className="font-medium">{request.car_mark}</span>
                          <span className="text-[#5A5A5A] ml-1">{request.car_number}</span>
                        </td>
                        {/* 3. Event Type */}
                        <td>
                          <span className="text-sm">{request.event_type || request.scope_of_work || '-'}</span>
                        </td>
                        {/* 4. Repair Site */}
                        <td>
                          <span className="text-sm">{request.repair_site || request.site_name || '-'}</span>
                        </td>
                        {/* 5. Requestor */}
                        <td>
                          <span className="text-sm">{request.requestor_name || request.requestor_company || '-'}</span>
                        </td>
                        {/* 6. Car Owner */}
                        <td>
                          <span className="text-sm">{request.car_owner || '-'}</span>
                        </td>
                        {/* 7. Days Open */}
                        <td>
                          <span
                            className="font-medium text-sm"
                            style={{ color: daysOpenColor }}
                          >
                            <Clock className="w-3.5 h-3.5 inline mr-1" />
                            {daysOpen}d
                          </span>
                        </td>
                        {/* 8. Status - Single status display based on source of truth */}
                        <td>
                          <div className="flex flex-col gap-1">
                            {/* Single Status: WS status if Work Schedule exists, otherwise SR status */}
                            {request.work_schedule_status ? (
                              // Work Schedule exists - show WS status as the source of truth
                              <span 
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getWsStatusConfig(request.work_schedule_status).color}`}
                                data-testid={`status-badge-${request.id}`}
                              >
                                {getWsStatusConfig(request.work_schedule_status).label}
                              </span>
                            ) : (
                              // No Work Schedule - show SR status (pre-scheduling lifecycle)
                              <span 
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
                                data-testid={`status-badge-${request.id}`}
                              >
                                {/* NEW status icon - match Dashboard star icon */}
                                {statusConfig.icon === 'Sparkles' && <Sparkles className="w-3 h-3" />}
                                {statusConfig.label}
                              </span>
                            )}
                            {/* Follow-up indicator only for pre-scheduling statuses without WS */}
                            {!request.work_schedule_status && (request.status === 'ESTIMATE' || request.status === 'APPROVAL') && showFollowup && (
                              <span className="flex items-center gap-1 text-xs text-orange-600 font-medium">
                                <AlertCircle className="w-3 h-3" />
                              </span>
                            )}
                            {/* Archived indicator */}
                            {request.archived && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                                Archived
                              </span>
                            )}
                          </div>
                        </td>
                        {/* 9. Actions — hidden for technicians (Wave 1 #5) */}
                        {!isTechnician && (
                        <td className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {/* Change Status Button - only show for SRs without Work Schedule (pre-scheduling) */}
                            {!request.work_schedule_status && request.status !== 'SR_SCHEDULED' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openStatusModal(request)}
                                className="text-xs"
                                data-testid={`change-status-${request.id}`}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Status
                              </Button>
                            )}
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`sr-actions-${request.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canManage && (
                                  <DropdownMenuItem onClick={() => handlePin(request.id)}>
                                    <Pin className="w-4 h-4 mr-2" />
                                    {request.pinned ? 'Unpin' : 'Pin to Top'}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => navigate(`/car-files/${request.car_mark}/${request.car_number}?sr=${request.id}&sr_number=${request.job_number}`)}
                                  data-testid={`view-car-file-${request.id}`}
                                >
                                  <FolderOpen className="w-4 h-4 mr-2" />
                                  View Car File
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the service request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Change Modal */}
      {selectedSR && (
        <StatusChangeModal
          open={statusModalOpen}
          onOpenChange={setStatusModalOpen}
          entityType="sr"
          currentStatus={selectedSR.status || 'NEW'}
          previousStatus={selectedSR.previous_status}
          entityId={selectedSR.id}
          entityLabel={`${selectedSR.job_number} - ${selectedSR.car_mark} ${selectedSR.car_number}`}
          daysInStatus={getDaysInStatus(selectedSR)}
          userRole={user?.role || 'OPERATOR'}
          onStatusChange={handleStatusChanged}
          api={api}
        />
      )}
    </div>
  );
};

export default ServiceRequestsPage;
